import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, Form, Button, Row, Col, Alert, Spinner, Table, Collapse, Badge, Modal } from "react-bootstrap";
import axios from "axios";

const getDefaultDateFrom = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date.toISOString().slice(0, 10);
};
const getDefaultDateTo = () => new Date().toISOString().slice(0, 10);
const formatApiError = (err, fallback) => {
  const error = err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
  const hint = err?.response?.data?.errorHint;
  return hint ? `${error}\n${hint}` : error;
};
const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const DetailRow = ({ label, value }) => (
  <tr>
    <th style={{ width: "35%" }}>{label}</th>
    <td>{displayValue(value)}</td>
  </tr>
);

const getInvoiceMetadata = (invoice) => invoice?.invoiceMetadata ?? invoice;

const getInvoiceKsefNumber = (invoice) => {
  const meta = getInvoiceMetadata(invoice);
  return meta?.ksefNumber ?? meta?.ksefReferenceNumber ?? meta?.invoiceReferenceNumber ?? null;
};

const KsefPage = () => {
  const { t } = useTranslation();
  const [apiKey, setApiKey] = useState("");
  const [dateFrom, setDateFrom] = useState(getDefaultDateFrom);
  const [dateTo, setDateTo] = useState(getDefaultDateTo);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [config, setConfig] = useState(null);
  const [showGetToken, setShowGetToken] = useState(false);
  const [challengeResult, setChallengeResult] = useState(null);
  const [authToken, setAuthToken] = useState("");
  const [refNumber, setRefNumber] = useState("");
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [obtainedTokens, setObtainedTokens] = useState(null);
  const [websiteToken, setWebsiteToken] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetailsByNumber, setInvoiceDetailsByNumber] = useState({});
  const [invoiceDetailsLoadingKey, setInvoiceDetailsLoadingKey] = useState(null);
  const [invoiceDetailsError, setInvoiceDetailsError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios.get("/api/ksef/config", { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ label: "KSeF PR", docsUrl: "https://api.ksef.mf.gov.pl/docs/v2/index.html" }));
  }, []);

  const fetchInvoices = async (useDemo = false) => {
    setError(null);
    setResponse(null);
    if (!apiKey.trim()) {
      setError(t("ksefErrorKeyRequired"));
      return;
    }
    if (!dateFrom || !dateTo) {
      setError(t("ksefErrorDatesRequired"));
      return;
    }
    if (new Date(dateFrom) > new Date(dateTo)) {
      setError(t("ksefErrorDateRange"));
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/ksef/invoices",
        {
          apiKey: apiKey.trim(),
          dateFrom,
          dateTo,
          useDemo,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      setResponse(response.data);
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        t("ksefErrorFetch");
      setError(message);
      setResponse(err.response?.data ? { ...err.response.data, error: message } : null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchInvoices(false);
  };

  const handleDemo = () => fetchInvoices(true);

  const getChallenge = async () => {
    setTokenError(null);
    setChallengeResult(null);
    setTokenLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await axios.post("/api/ksef/auth/challenge", { useDemo: false }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setChallengeResult(r.data);
      console.log("[KSeF] Challenge:", r.data);
    } catch (err) {
      setTokenError(formatApiError(err, "Challenge failed"));
    } finally {
      setTokenLoading(false);
    }
  };

  const getAccessToken = async () => {
    if (!authToken.trim() || !refNumber.trim()) {
      setTokenError("Введіть authenticationToken та referenceNumber");
      return;
    }
    setTokenError(null);
    setObtainedTokens(null);
    setTokenLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await axios.post(
        "/api/ksef/auth/redeem",
        { authenticationToken: authToken.trim(), referenceNumber: refNumber.trim(), useDemo: false },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setObtainedTokens({ accessToken: r.data.accessToken, refreshToken: r.data.refreshToken });
    } catch (err) {
      setTokenError(formatApiError(err, "Redeem failed"));
    } finally {
      setTokenLoading(false);
    }
  };

  const applyAccessToken = () => {
    if (obtainedTokens?.accessToken) {
      setApiKey(obtainedTokens.accessToken);
      setShowGetToken(false);
    }
  };

  const getAccessTokenFromWebsiteToken = async () => {
    if (!websiteToken.trim()) {
      setTokenError(t("ksefWebsiteTokenRequired"));
      return;
    }
    setTokenError(null);
    setObtainedTokens(null);
    setTokenLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await axios.post(
        "/api/ksef/auth/login-with-ksef-token",
        { ksefTokenFromWebsite: websiteToken.trim(), useDemo: false },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setObtainedTokens({ accessToken: r.data.accessToken, refreshToken: r.data.refreshToken });
    } catch (err) {
      setTokenError(formatApiError(err, "KSeF token login failed"));
    } finally {
      setTokenLoading(false);
    }
  };

  const invoices = Array.isArray(response?.invoices) ? response.invoices : [];
  const selectedMeta = getInvoiceMetadata(selectedInvoice);
  const selectedKsefNumber = getInvoiceKsefNumber(selectedInvoice);
  const selectedInvoiceDetails = selectedKsefNumber ? invoiceDetailsByNumber[selectedKsefNumber] : null;

  const loadInvoiceDetails = async (invoice) => {
    const ksefNumber = getInvoiceKsefNumber(invoice);
    if (!ksefNumber || !apiKey.trim() || invoiceDetailsByNumber[ksefNumber]) {
      return;
    }

    setInvoiceDetailsError(null);
    setInvoiceDetailsLoadingKey(ksefNumber);

    try {
      const token = localStorage.getItem("token");
      const detailResponse = await axios.post(
        "/api/ksef/invoice-details",
        {
          apiKey: apiKey.trim(),
          ksefNumber,
          useDemo: response?.environment === "demo",
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      setInvoiceDetailsByNumber((prev) => ({
        ...prev,
        [ksefNumber]: detailResponse.data,
      }));
    } catch (err) {
      setInvoiceDetailsError(formatApiError(err, "Не вдалося завантажити повну фактуру"));
    } finally {
      setInvoiceDetailsLoadingKey((current) => (current === ksefNumber ? null : current));
    }
  };

  const handleInvoiceClick = (invoice) => {
    setSelectedInvoice(invoice);
    loadInvoiceDetails(invoice);
  };

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
        <h2 className="mb-0">{t("ksefTitle")}</h2>
        {config && (
          <>
            <Badge bg={config.environment === "demo" ? "info" : "secondary"}>
              {config.label}
            </Badge>
            {config.docsUrl && (
              <a href={config.docsUrl} target="_blank" rel="noopener noreferrer" className="small">
                Документація API →
              </a>
            )}
          </>
        )}
      </div>
      <p className="text-muted mb-4">{t("ksefDescription")}</p>

      <Card className="mb-4">
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer" }}
          onClick={() => setShowGetToken(!showGetToken)}
        >
          <span>{t("ksefGetTokenTitle")}</span>
          <Badge bg="secondary">{showGetToken ? "−" : "+"}</Badge>
        </Card.Header>
        <Collapse in={showGetToken}>
          <Card.Body className="bg-light">
            <p className="small fw-bold mb-2">{t("ksefWebsiteTokenTitle")}</p>
            <p className="small text-muted mb-2">{t("ksefWebsiteTokenHint")}</p>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="20260310-EC-...|nip-7822599564|..."
                value={websiteToken}
                onChange={(e) => setWebsiteToken(e.target.value)}
                className="font-monospace small"
              />
            </Form.Group>
            <Button size="sm" variant="primary" onClick={getAccessTokenFromWebsiteToken} disabled={tokenLoading} className="mb-4">
              {tokenLoading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
              {t("ksefGetTokenFromWebsite")}
            </Button>

            <hr className="my-3" />
            <p className="small text-muted mb-3">{t("ksefGetTokenIntro")}</p>
            <div className="mb-3">
              <Button size="sm" variant="outline-primary" onClick={getChallenge} disabled={tokenLoading}>
                {tokenLoading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                1. {t("ksefGetTokenChallenge")}
              </Button>
              {challengeResult && (
                <div className="mt-2 small">
                  <strong>Challenge:</strong> <code className="bg-white px-1">{challengeResult.challenge?.slice?.(0, 40)}…</code>
                  {challengeResult.timestamp && <span className="ms-2">Timestamp: {String(challengeResult.timestamp).slice(0, 19)}</span>}
                  {challengeResult.timestampMs && <span className="ms-2">timestampMs: {challengeResult.timestampMs}</span>}
                </div>
              )}
            </div>
            <p className="small text-muted mb-2">{t("ksefGetTokenSignHint")}</p>
            <Form.Group className="mb-2">
              <Form.Label className="small">authenticationToken</Form.Label>
              <Form.Control
                size="sm"
                type="password"
                placeholder="eyJ…"
                value={authToken}
                onChange={(e) => setAuthToken(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small">referenceNumber</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder=""
                value={refNumber}
                onChange={(e) => setRefNumber(e.target.value)}
              />
            </Form.Group>
            <Button size="sm" variant="outline-success" onClick={getAccessToken} disabled={tokenLoading}>
              {tokenLoading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
              2. {t("ksefGetTokenRedeem")}
            </Button>
            {tokenError && (
              <Alert variant="danger" className="mt-2 mb-0 small" style={{ whiteSpace: "pre-line" }}>
                {tokenError}
              </Alert>
            )}
            {obtainedTokens?.accessToken && (
              <div className="mt-3 p-2 bg-white rounded border">
                <div className="small text-success mb-2">accessToken отримано</div>
                <Button size="sm" variant="primary" onClick={applyAccessToken}>
                  {t("ksefGetTokenApply")}
                </Button>
              </div>
            )}
          </Card.Body>
        </Collapse>
      </Card>

      <Card className="mb-4">
        <Card.Header>{t("ksefParams")}</Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>{t("ksefApiKey")}</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="password"
                  placeholder={t("ksefApiKeyPlaceholder")}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  autoComplete="off"
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>{t("ksefDateFrom")}</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>{t("ksefDateTo")}</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Col>
            </Form.Group>

            {error && (
              <Alert variant="danger" className="mb-3" onClose={() => setError(null)} dismissible>
                <div>{error}</div>
                {response?.errorHint && (
                  <div className="mt-2 small">{response.errorHint}</div>
                )}
              </Alert>
            )}

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t("ksefLoading")}
                  </>
                ) : (
                  t("ksefFetchButton")
                )}
              </Button>
              <Button
                type="button"
                variant="outline-info"
                disabled={loading}
                onClick={handleDemo}
                title="Запит до api-demo.ksef.mf.gov.pl (KSeF TR)"
              >
                {t("ksefDemoButton")}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {response && !response.error && (
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="d-flex align-items-center gap-2">
              {t("ksefResults")} — {invoices.length > 0 ? t("ksefInvoicesCount", { count: invoices.length }) : t("ksefNoInvoices")}
              {response.environment === "demo" && (
                <Badge bg="info">KSeF TR (демо)</Badge>
              )}
            </span>
            <Button variant="outline-secondary" size="sm" onClick={() => setShowRaw(!showRaw)}>
              {showRaw ? t("ksefHideRaw") : t("ksefShowRaw")}
            </Button>
          </Card.Header>
          <Card.Body>
            {invoices.length > 0 ? (
              <Table responsive striped bordered className="mb-0">
                <thead>
                  <tr>
                    <th>{t("ksefTableNumber")}</th>
                    <th>{t("ksefTableDate")}</th>
                    <th>{t("ksefTableSubject")}</th>
                    <th>KSeF ID</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, idx) => {
                    const meta = inv.invoiceMetadata ?? inv;
                    const ref = meta.invoiceNumber ?? meta.referenceNumber ?? meta.number ?? meta.ksefReferenceNumber ?? inv.referenceNumber ?? "—";
                    const date = meta.issueDate ?? meta.issueDateTime ?? meta.date ?? "—";
                    const subject =
                      meta.buyer?.name ??
                      meta.seller?.name ??
                      meta.subjectName ??
                      meta.sellerName ??
                      meta.subject ??
                      (meta.subject1 ?? meta.subject2)?.name ??
                      "—";
                    const ksefId = meta.ksefNumber ?? meta.ksefReferenceNumber ?? meta.ksefNumber ?? meta.invoiceReferenceNumber ?? "—";
                    return (
                      <tr
                        key={idx}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleInvoiceClick(inv)}
                        title="Показати деталі фактури"
                      >
                        <td>{ref}</td>
                        <td>{typeof date === "string" ? date.slice(0, 10) : date}</td>
                        <td>{typeof subject === "object" ? JSON.stringify(subject) : subject}</td>
                        <td className="small">{ksefId}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <p className="text-muted mb-0">{t("ksefNoInvoices")}</p>
            )}
            <Collapse in={showRaw}>
              <div className="mt-3">
                <pre className="bg-dark text-light p-3 rounded small mb-0" style={{ maxHeight: "300px", overflow: "auto" }}>
                  {JSON.stringify(response, null, 2)}
                </pre>
              </div>
            </Collapse>
          </Card.Body>
        </Card>
      )}

      <Modal show={Boolean(selectedInvoice)} onHide={() => setSelectedInvoice(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedMeta?.invoiceNumber || selectedMeta?.ksefNumber || "KSeF invoice"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMeta && (
            <>
              <div className="mb-3 d-flex flex-wrap gap-2">
                <Badge bg="secondary">{selectedMeta.querySubjectType || "Subject"}</Badge>
                {selectedMeta.invoiceType && <Badge bg="info">{selectedMeta.invoiceType}</Badge>}
                {selectedMeta.invoicingMode && <Badge bg="dark">{selectedMeta.invoicingMode}</Badge>}
                {selectedMeta.currency && <Badge bg="light" text="dark">{selectedMeta.currency}</Badge>}
                {selectedInvoiceDetails?.xmlHash && <Badge bg="warning" text="dark">XML hash: {selectedInvoiceDetails.xmlHash.slice(0, 12)}…</Badge>}
              </div>

              {invoiceDetailsLoadingKey === selectedKsefNumber && (
                <Alert variant="info" className="d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" />
                  <span>Завантажую повну структуру XML фактури з KSeF…</span>
                </Alert>
              )}

              {invoiceDetailsError && (
                <Alert variant="warning" style={{ whiteSpace: "pre-line" }}>
                  {invoiceDetailsError}
                </Alert>
              )}

              <h6 className="mb-2">Основне</h6>
              <Table bordered size="sm" className="mb-3">
                <tbody>
                  <DetailRow label="KSeF ID" value={selectedMeta.ksefNumber} />
                  <DetailRow label="Номер фактури" value={selectedMeta.invoiceNumber} />
                  <DetailRow label="Тип фактури" value={selectedMeta.invoiceType} />
                  <DetailRow label="Роль у запиті" value={selectedMeta.querySubjectType} />
                  <DetailRow label="Режим виставлення" value={selectedMeta.invoicingMode} />
                  <DetailRow label="Валюта" value={selectedMeta.currency} />
                  <DetailRow label="Є вкладення" value={selectedMeta.hasAttachment} />
                  <DetailRow label="Самофактурування" value={selectedMeta.isSelfInvoicing} />
                  <DetailRow label="Хеш фактури" value={selectedMeta.invoiceHash} />
                </tbody>
              </Table>

              <h6 className="mb-2">Дати</h6>
              <Table bordered size="sm" className="mb-3">
                <tbody>
                  <DetailRow label="Дата виставлення" value={selectedMeta.issueDate} />
                  <DetailRow label="Дата інвойсингу" value={selectedMeta.invoicingDate} />
                  <DetailRow label="Дата отримання" value={selectedMeta.acquisitionDate} />
                  <DetailRow label="Дата постійного зберігання" value={selectedMeta.permanentStorageDate} />
                </tbody>
              </Table>

              <h6 className="mb-2">Суми</h6>
              <Table bordered size="sm" className="mb-3">
                <tbody>
                  <DetailRow label="Net" value={selectedMeta.netAmount} />
                  <DetailRow label="VAT" value={selectedMeta.vatAmount} />
                  <DetailRow label="Gross" value={selectedMeta.grossAmount} />
                </tbody>
              </Table>

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <h6 className="mb-2">Продавець</h6>
                  <Table bordered size="sm" className="mb-0">
                    <tbody>
                      <DetailRow label="NIP" value={selectedMeta.seller?.nip} />
                      <DetailRow label="Назва" value={selectedMeta.seller?.name} />
                    </tbody>
                  </Table>
                </Col>
                <Col md={6}>
                  <h6 className="mb-2">Покупець</h6>
                  <Table bordered size="sm" className="mb-0">
                    <tbody>
                      <DetailRow label="Назва" value={selectedMeta.buyer?.name} />
                      <DetailRow label="Тип ідентифікатора" value={selectedMeta.buyer?.identifier?.type} />
                      <DetailRow label="Ідентифікатор" value={selectedMeta.buyer?.identifier?.value} />
                    </tbody>
                  </Table>
                </Col>
              </Row>

              <h6 className="mb-2">Form Code</h6>
              <Table bordered size="sm" className="mb-3">
                <tbody>
                  <DetailRow label="System code" value={selectedMeta.formCode?.systemCode} />
                  <DetailRow label="Schema version" value={selectedMeta.formCode?.schemaVersion} />
                  <DetailRow label="Value" value={selectedMeta.formCode?.value} />
                </tbody>
              </Table>

              {Array.isArray(selectedMeta.thirdSubjects) && selectedMeta.thirdSubjects.length > 0 && (
                <>
                  <h6 className="mb-2">Third Subjects</h6>
                  <Table bordered size="sm" className="mb-3">
                    <thead>
                      <tr>
                        <th>Назва</th>
                        <th>Тип</th>
                        <th>Значення</th>
                        <th>Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedMeta.thirdSubjects.map((item, index) => (
                        <tr key={index}>
                          <td>{displayValue(item?.name)}</td>
                          <td>{displayValue(item?.identifier?.type)}</td>
                          <td>{displayValue(item?.identifier?.value)}</td>
                          <td>{displayValue(item?.role)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              <h6 className="mb-2">Додатковий опис / коментарі з XML</h6>
              {selectedInvoiceDetails?.extracted?.additionalDescriptions?.length > 0 ? (
                <Table bordered size="sm" className="mb-3">
                  <thead>
                    <tr>
                      <th>Поле</th>
                      <th>Шлях</th>
                      <th>Значення</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoiceDetails.extracted.additionalDescriptions.map((item, index) => (
                      <tr key={`${item.path}-${index}`}>
                        <td>{displayValue(item.key)}</td>
                        <td className="small">{displayValue(item.path)}</td>
                        <td style={{ whiteSpace: "pre-wrap" }}>{displayValue(item.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted small">
                  Поки не знайдено окремих полів `Opis` / `Komentarz`. Нижче є повна XML-структура для перевірки.
                </p>
              )}

              {Array.isArray(selectedInvoiceDetails?.extracted?.lineItemCandidates) &&
                selectedInvoiceDetails.extracted.lineItemCandidates.length > 0 && (
                  <>
                    <h6 className="mb-2">Позиції / блоки з XML</h6>
                    <pre className="bg-light p-3 rounded small mb-3" style={{ maxHeight: "260px", overflow: "auto" }}>
                      {JSON.stringify(selectedInvoiceDetails.extracted.lineItemCandidates, null, 2)}
                    </pre>
                  </>
                )}

              {Array.isArray(selectedInvoiceDetails?.extracted?.interestingTextNodes) &&
                selectedInvoiceDetails.extracted.interestingTextNodes.length > 0 && (
                  <>
                    <h6 className="mb-2">Знайдені текстові поля з описами</h6>
                    <pre className="bg-light p-3 rounded small mb-3" style={{ maxHeight: "260px", overflow: "auto" }}>
                      {JSON.stringify(selectedInvoiceDetails.extracted.interestingTextNodes, null, 2)}
                    </pre>
                  </>
                )}

              {selectedInvoiceDetails?.parsedInvoice && (
                <>
                  <h6 className="mb-2">Повна структура XML (parsed)</h6>
                  <pre className="bg-dark text-light p-3 rounded small mb-3" style={{ maxHeight: "320px", overflow: "auto" }}>
                    {JSON.stringify(selectedInvoiceDetails.parsedInvoice, null, 2)}
                  </pre>
                </>
              )}

              {selectedInvoiceDetails?.rawXml && (
                <>
                  <h6 className="mb-2">Raw XML</h6>
                  <pre className="bg-dark text-light p-3 rounded small mb-3" style={{ maxHeight: "320px", overflow: "auto" }}>
                    {selectedInvoiceDetails.rawXml}
                  </pre>
                </>
              )}

              <h6 className="mb-2">Raw JSON</h6>
              <pre className="bg-dark text-light p-3 rounded small mb-0" style={{ maxHeight: "320px", overflow: "auto" }}>
                {JSON.stringify(selectedMeta, null, 2)}
              </pre>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default KsefPage;
